import React from "react";
import Switch from "../Switch";
import Dropdown from "../Dropdown";
import MultipleSelectDropdown from "../MultipleSelectDropdown";
import InputField from "../InputField";

/**
 * The mailbox wishes of an offboarding: shared mailbox, forwarding and autoreply.
 *
 * The pickers only appear once their toggle is on, so the orderer is never asked who
 * should have access to a mailbox that is not being converted.
 *
 * @param {object} props
 * @param {object} props.value - The current offboarding form value
 * @param {{userPublicId: string, label: string}[]} props.otherUserOptions - The
 *   colleagues that may take over the mailbox; never the user being offboarded
 * @param {(changes: object) => void} props.onFieldsChange - Applies changed fields
 * @returns {JSX.Element} The mailbox part of the offboarding form
 */
const MailboxWishesSection = ({ value, otherUserOptions, onFieldsChange }) => {
  const forwardToLabel =
    otherUserOptions.find(
      (option) => option.userPublicId === value.forwardToUserPublicId
    )?.label || "";

  const handleForwardToSelect = (label) => {
    const chosen = otherUserOptions.find((option) => option.label === label);
    onFieldsChange({ forwardToUserPublicId: chosen ? chosen.userPublicId : "" });
  };

  return (
    <div className="flex flex-col gap-4">
      <Switch
        label="Convert to shared mailbox"
        checked={value.convertToSharedMailbox}
        onChange={(checked) =>
          onFieldsChange({ convertToSharedMailbox: checked })
        }
      />

      {value.convertToSharedMailbox && (
        <MultipleSelectDropdown
          label="Who should have access"
          options={otherUserOptions.map((option) => ({
            label: option.label,
            value: option.userPublicId,
          }))}
          selected={value.sharedMailboxAccessUserPublicIds}
          onChange={(selectedIds) =>
            onFieldsChange({ sharedMailboxAccessUserPublicIds: selectedIds })
          }
          placeholder="Choose who should have access"
          searchable={true}
        />
      )}

      <Switch
        label="Forward mail"
        checked={value.forwardMail}
        onChange={(checked) => onFieldsChange({ forwardMail: checked })}
      />

      {value.forwardMail && (
        <Dropdown
          label="Forward to"
          value={forwardToLabel}
          options={otherUserOptions.map((option) => option.label)}
          onSelect={handleForwardToSelect}
        />
      )}

      <InputField
        label="Autoreply text"
        value={value.autoReplyText}
        onChange={(event) =>
          onFieldsChange({ autoReplyText: event.target.value })
        }
        placeholder="Enter the autoreply the mailbox should send"
        type="text-area"
      />
    </div>
  );
};

export default MailboxWishesSection;
